import { BaseService } from './BaseService';
import {
  InjectionHistoryPageableQueryResult,
  InjectionHistoryStrategyViewModel,
  InjectionHistoryTrendDataViewModel,
  InjectionHistoryAgeTrendDataResponse
} from '../models/injection';
import { AnimalType } from '../models/animal';

export class InjectionsService extends BaseService {
  getInjectionHistory = async (
    id: string,
    page: number,
    size: number
  ): Promise<InjectionHistoryPageableQueryResult> => {
    const query = this.BuildQueryString({ EntityId: id, page, size });

    const result = await this.Fetch(`/injections?${query}`, 'GET');

    return this.FromResult<InjectionHistoryPageableQueryResult>(result);
  };

  getAnimalInjectionStrategy = async (
    animalType: AnimalType,
    weekNumber: number,
    year: number
  ): Promise<InjectionHistoryStrategyViewModel> => {
    const query = this.BuildQueryString({ animalType, weekNumber, year });

    const result = await this.Fetch(`/injections/strategy?${query}`, 'GET');

    return this.FromResult<InjectionHistoryStrategyViewModel>(result);
  };

  getAnimalInjectionTrendData = async (
    animalType: AnimalType,
    yearsStart: number,
    yearsEnd: number,
    supplyId?: string,
    pregnantOnly?: boolean,
    lactatingOnly?: boolean,
    weekStart?: number,
    weekEnd?: number
  ): Promise<InjectionHistoryTrendDataViewModel> => {
    const query = this.BuildQueryString({
      animalType,
      'Years.Start': yearsStart,
      'Years.End': yearsEnd,
      supplyId,
      pregnantOnly,
      lactatingOnly,
      'Week.Start': weekStart,
      'Week.End': weekEnd
    });

    const result = await this.Fetch(`/injections/trend-data?${query}`, 'GET');

    return this.FromResult<InjectionHistoryTrendDataViewModel>(result);
  };

  getAnimalInjectionAgeTrendData = async (
    animalType: AnimalType,
    year: number,
    weekNumber: number,
    supplyId?: string,
    pregnantOnly?: boolean,
    lactatingOnly?: boolean
  ): Promise<InjectionHistoryAgeTrendDataResponse> => {
    const query = this.BuildQueryString({ animalType, year, weekNumber, supplyId, pregnantOnly, lactatingOnly });

    const result = await this.Fetch(`/injections/age-trend-data?${query}`, 'GET');

    return this.FromResult<InjectionHistoryAgeTrendDataResponse>(result);
  };

  createOralFeedInjection = async (
    farmSupplyId: string,
    locationsIds: string[],
    repeatDays: number,
    dose: number
  ): Promise<string[]> => {
    const command = JSON.stringify({ farmSupplyId, locationsIds, repeatDays, dose });

    const result = await this.Fetch('/injections/oralfeed', 'POST', command);

    return this.FromResult<string[]>(result);
  };

  // Vaccination flow: same dose/supply logic as oral feed, but the backend
  // filters each location's animals down to gilts only. The endpoint works per
  // location (locationId in the path), so we call it once per selected location
  // and aggregate the created injections. Locations with no gilts come back with
  // an empty injectionsCreated array and are reported back as skipped.
  createVaccinationInjection = async (
    farmMedicationSupplyId: string,
    locationsIds: string[],
    repeatDays: number,
    dose: number
  ): Promise<CreateVaccinationInjectionResult> => {
    const body = JSON.stringify({ farmMedicationSupplyId, repeatDays, dose });

    const injectionsCreated: string[] = [];
    const skippedLocationIds: string[] = [];

    for (const locationId of locationsIds) {
      const result = await this.Fetch(`/injections/vaccination/location/${locationId}`, 'POST', body);
      const response = await this.FromResult<{ injectionsCreated: string[] }>(result);
      const created = response?.injectionsCreated ?? [];

      if (created.length === 0) {
        skippedLocationIds.push(locationId);
      } else {
        injectionsCreated.push(...created);
      }
    }

    return { injectionsCreated, skippedLocationIds };
  };
}

export interface CreateVaccinationInjectionResult {
  injectionsCreated: string[];
  skippedLocationIds: string[];
}

export default InjectionsService;
